import { computed, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import type {
  RoamingCountryStruct,
  RoamingZoneStruct,
} from "@/struct/RoaminStruct";
import { useStore } from "@/store";

interface RoamingInfoProps {
  country: RoamingCountryStruct;
  zone: RoamingZoneStruct | null;
  othersCountriesCount: number;
}

export function useRoamingInfoDetails(props: RoamingInfoProps) {
  const { t } = useI18n();
  const store = useStore();

  const isShowOthers = ref(false);
  const isLoadingActivate = ref<boolean>(false);
  const isUnsuccessful = ref<boolean>(false);
  const isSuccessful = ref<boolean>(false);
  const isUnhandledError = ref<boolean>(false);
  const isIncompatible = ref<boolean>(false);
  const isAlreadyAdded = ref<boolean>(false);

  const conditions = computed(() => [
    {
      title: t("roaming.details.sections.internet"),
      options: [
        {
          text: t("roaming.details.options.trafficMb"),
          value: props.zone?.data_mb_price || 0,
        },
      ],
    },
    {
      title: t("roaming.details.sections.calls"),
      options: [
        {
          text: t("roaming.details.options.outgoingRussia"),
          value: props.zone?.group_1_call_price || 0,
        },
        {
          text: t("roaming.details.options.outgoingCis"),
          value: props.zone?.group_2_call_price || 0,
        },
        {
          text: t("roaming.details.options.outgoingEurope"),
          value: props.zone?.group_3_call_price || 0,
        },
        {
          text: t("roaming.details.options.outgoingOther"),
          value: props.zone?.group_4_call_price || 0,
        },
        {
          text: t("roaming.details.options.incomingAll"),
          value: props.zone?.incoming_call_price || 0,
        },
      ],
    },
    {
      title: t("roaming.details.sections.sms"),
      options: [
        {
          text: t("roaming.details.options.outgoing"),
          value: props.zone?.group_2_sms_price || 0,
        },
      ],
    },
  ]);

  const othersCountriesCountFormat = computed(() => {
    const count = props.othersCountriesCount;
    const word =
      count === 1
        ? t("roaming.details.countriesOne")
        : t("roaming.details.countriesMany");
    return `${count} ${word}`;
  });

  const formatAmount = (amount: string | number) => {
    if (amount && typeof amount === "string") {
      return (
        Number(amount).toLocaleString("ru-RU", {
          minimumFractionDigits: 2,
        }) + "₽"
      );
    } else if (amount && typeof amount === "number") {
      return amount.toLocaleString("ru-RU", { minimumFractionDigits: 2 }) + "₽";
    }
    return "";
  };

  const toggleCheckOthers = () => {
    isShowOthers.value = !isShowOthers.value;
  };

  const closeCheckOthers = () => {
    isShowOthers.value = false;
  };

  const closeAllModal = () => {
    isUnsuccessful.value = false;
    isSuccessful.value = false;
    isUnhandledError.value = false;
    isIncompatible.value = false;
    isAlreadyAdded.value = false;
  };

  const toPayment = () => {
    window.open("https://sberbank.com/sms/mybill?cs=2116856134125", "_blank");
  };

  const getProductId = () => {
    return process.env.VUE_APP_CURRENT_BRANCH === "prod" ? 21 : 57;
  };

  const activate = () => {
    isLoadingActivate.value = true;
    store
      .dispatch("product/addProduct", getProductId())
      .then(() => {
        isSuccessful.value = true;
      })
      .catch((err) => {
        switch (err.response.data.code) {
          case 57:
            isIncompatible.value = true;
            break;
          case 58:
            isAlreadyAdded.value = true;
            break;
          case 62:
            isUnsuccessful.value = true;
            break;
          default:
            isUnhandledError.value = true;
        }
      })
      .finally(() => {
        isLoadingActivate.value = false;
      });
  };

  watch(
    () => props.country,
    () => {
      isShowOthers.value = false;
    }
  );

  return {
    activate,
    closeAllModal,
    closeCheckOthers,
    conditions,
    formatAmount,
    isAlreadyAdded,
    isIncompatible,
    isLoadingActivate,
    isShowOthers,
    isSuccessful,
    isUnhandledError,
    isUnsuccessful,
    othersCountriesCountFormat,
    toPayment,
    toggleCheckOthers,
  };
}
